/* «Levering og fragt» (snippets/delivery-panel.liquid).
   - Postcode lookup: Shopify's live rates for the cart as it is (/cart/shipping_rates.json).
     Shopify cannot price an empty cart; the panel then says so and keeps the shop's own list.
     A newer lookup cancels an older one, so a slow answer never overwrites a fresh one.
   - «… tilbage»: how long is left of today's dispatch cut-off, next to the fixed time the
     estimate already names. Shown only on a dispatch day before the cut-off. */

const t = (key, vars = {}) =>
  Object.entries(vars).reduce(
    (text, [name, value]) => text.replaceAll(`%%${name.toUpperCase()}%%`, String(value)),
    window.Munkwell?.strings?.delivery?.[key] || ''
  );

const money = (amount, format) => {
  const value = Number(amount);
  const fixed = value.toFixed(2);
  const [int, dec] = fixed.split('.');
  const dotted = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (format || '{{amount}} kr')
    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, `${dotted},${dec}`)
    .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, dotted)
    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, int.replace(/\B(?=(\d{3})+(?!\d))/g, ','))
    .replace(/\{\{\s*amount\s*\}\}/, `${int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${dec}`);
};

const days = (rate) => {
  const d = Array.isArray(rate.delivery_days) ? rate.delivery_days.filter(Number.isFinite) : [];
  if (!d.length) return '';
  const lo = Math.min(...d);
  const hi = Math.max(...d);
  if (lo !== hi) return t('daysRange', { min: lo, max: hi });
  return t(lo === 1 ? 'daysOne' : 'daysOther', { count: lo });
};

const CARRIERS = [
  { id: 'dao', name: 'DAO', test: /\bdao\b/i },
  { id: 'gls', name: 'GLS', test: /\bgls\b/i },
  { id: 'postnord', name: 'PostNord', test: /post\s*nord/i },
];

/* The free-shipping line follows the offer ladder's choice: assets/product-offer.js sends
   mw:offer:change with the chosen total, and «mangler X til fri fragt» is worked out from that
   (it used to stay on the price the page was rendered with). */
document.addEventListener('mw:offer:change', (event) => {
  const total = Number(event.detail?.total) || 0;
  document.querySelectorAll('[data-free]').forEach((box) => {
    const threshold = Number(box.dataset.threshold) || 0;
    const text = box.querySelector('[data-free-text]');
    if (!threshold || !text) return;
    const sum = (Number(box.dataset.cart) || 0) + total;
    const remaining = threshold - sum;
    const format = box.closest('[data-money-format]')?.dataset.moneyFormat;
    const amount = money(remaining / 100, format).replace(/,00(?=\D|$)/, '');
    box.classList.toggle('is-free', remaining <= 0);
    text.innerHTML = remaining <= 0 ? text.dataset.yes : text.dataset.no.replace('%%AMOUNT%%', amount);
    box.querySelector('[data-free-bar]')?.style.setProperty('--dpanel-pct', `${Math.min(100, Math.round((sum * 100) / threshold))}%`);
  });
});

class MwDeliveryPanel extends HTMLElement {
  connectedCallback() {
    if (this.wired) return;
    this.wired = true;
    this.form = this.querySelector('[data-panel-form]');
    this.zip = this.querySelector('[data-panel-zip]');
    this.msg = this.querySelector('[data-panel-msg]');
    this.house = this.querySelector('[data-panel-house]');
    this.live = this.querySelector('[data-panel-live]');
    this.seq = 0;
    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.lookup();
    });
    this.left();
    this.timer = setInterval(() => this.left(), 30000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.controller?.abort();
  }

  say(text) {
    if (!this.msg) return;
    this.msg.textContent = text;
    this.msg.hidden = !text;
  }

  left() {
    const est = this.querySelector('[data-deliv]');
    const out = this.querySelector('[data-deliv-left]');
    if (!est || !out) return;
    const now = new Date();
    const cutoff = Number(est.dataset.cutoff) || 14;
    const end = new Date(now.getFullYear(), now.getMonth(), now.getDate(), cutoff, 0, 0);
    const minutes = Math.floor((end - now) / 60000);
    const show = est.classList.contains('is-today') && minutes > 0;
    out.hidden = !show;
    if (!show) return;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    out.textContent = ` · ${h > 0 ? t('leftHours', { hours: h, minutes: m }) : t('leftMinutes', { minutes: m })}`;
  }

  async lookup() {
    // Four digits in Denmark and Norway, five in Sweden («123 45», spaces dropped).
    const five = this.dataset.zipLength === '5';
    const post = (this.zip?.value || '').replace(/\s+/g, '');
    if (!(five ? /^\d{5}$/ : /^\d{4}$/).test(post)) {
      this.say(t(five ? 'zipInvalid5' : 'zipInvalid'));
      this.zip?.focus();
      return;
    }
    const seq = ++this.seq;
    this.controller?.abort();
    this.controller = new AbortController();
    this.classList.add('is-busy');
    this.say(t('loading'));
    try {
      const root = window.Munkwell?.routes?.cart || '/cart';
      const url = `${root}/shipping_rates.json?shipping_address%5Bzip%5D=${post}&shipping_address%5Bcountry%5D=${encodeURIComponent(this.dataset.country || 'DK')}&shipping_address%5Bprovince%5D=`;
      const cartState = await fetch(`${root}.js`, { headers: { Accept: 'application/json' }, signal: this.controller.signal }).then((r) => r.json());
      if (seq !== this.seq) return;
      if (!cartState.item_count) {
        this.say(t('emptyCart'));
        return;
      }
      const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: this.controller.signal });
      if (seq !== this.seq) return;
      if (!response.ok) {
        this.say(t(response.status === 422 ? (five ? 'zipUnknown5' : 'zipUnknown') : 'error'));
        return;
      }
      const data = await response.json();
      const rates = (data.shipping_rates || []).slice().sort((a, b) => Number(a.price) - Number(b.price));
      if (!rates.length) {
        this.say(t('none'));
        return;
      }
      const format = this.dataset.moneyFormat;
      let marks = {};
      try {
        marks = JSON.parse(this.dataset.carriers || '{}');
      } catch (e) {}
      this.live.replaceChildren(
        ...rates.map((rate) => {
          const li = document.createElement('li');
          li.className = 'dpanel__rate';
          const label = rate.name || rate.presentment_name || t('fallbackName');
          const carrier = CARRIERS.find((c) => c.test.test(label));
          if (carrier && marks[carrier.id]) {
            li.classList.add('dpanel__rate--mark');
            const img = document.createElement('img');
            img.className = 'dpanel__mark';
            img.src = marks[carrier.id];
            img.alt = carrier.name;
            img.width = 32;
            img.height = 32;
            li.append(img);
          }
          const name = document.createElement('span');
          name.className = 'dpanel__ratename';
          // The mark says who; the text says how («Pakkeshop», «Hjemmelevering»).
          name.textContent = carrier ? label.replace(carrier.test, '').trim() || label : label;
          const price = document.createElement('span');
          price.className = 'dpanel__rateprice';
          const free = Number(rate.price) === 0;
          price.textContent = free ? t('free') : money(rate.price, format);
          if (free) price.classList.add('is-free');
          const text = document.createElement('span');
          text.className = 'dpanel__ratetext';
          text.append(name);
          const note = days(rate);
          if (note) {
            const n = document.createElement('span');
            n.className = 'dpanel__ratenote';
            n.textContent = note;
            text.append(n);
          }
          li.append(text, price);
          return li;
        })
      );
      this.live.hidden = false;
      if (this.house) this.house.hidden = true;
      this.say(t('result', { zip: post }));
    } catch (error) {
      if (error.name !== 'AbortError') this.say(t('error'));
    } finally {
      if (seq === this.seq) this.classList.remove('is-busy');
    }
  }
}

if (!customElements.get('mw-delivery-panel')) customElements.define('mw-delivery-panel', MwDeliveryPanel);
